/**
 * QMS Ticket Link Selector Component
 * Allows selecting tickets to link during wizard ticket creation
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, X, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useSearchLinkableTickets } from '../../hooks/useQmsTicket';
import { useSession } from '@/hooks/useSession';
import { getQmsOrganisationId } from '../../utils/organisation';
import type { QmsTicket, QmsTicketType, QmsRelationshipType } from '../../types/qms.types';

export interface PendingLink {
  ticket_id: string;
  ticket_title: string;
  ticket_type: QmsTicketType;
  relationship: QmsRelationshipType;
}

interface QmsTicketLinkSelectorProps {
  selectedLinks: PendingLink[];
  onLinksChange: (links: PendingLink[]) => void;
  disabled?: boolean;
}

export function QmsTicketLinkSelector({
  selectedLinks,
  onLinksChange,
  disabled = false,
}: QmsTicketLinkSelectorProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const qmsOrganisation = getQmsOrganisationId(user?.organisation);

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState<QmsRelationshipType>('related_to');

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Clear search when collapsing
  const handleToggleExpand = () => {
    if (isExpanded) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
    setIsExpanded(!isExpanded);
  };

  // Get IDs of already selected tickets to exclude from search
  const excludeLinkedIds = useMemo(() => {
    return selectedLinks.map((link) => link.ticket_id);
  }, [selectedLinks]);

  // Search query for linkable tickets (uses debounced term)
  const { data: searchResults = [], isLoading: isSearching } = useSearchLinkableTickets(
    qmsOrganisation,
    debouncedSearchTerm,
    undefined, // No current ticket to exclude (we're creating a new one)
    excludeLinkedIds
  );

  const relationshipOptions: Array<{ value: QmsRelationshipType; label: string }> = [
    { value: 'related_to', label: t('links.relationships.related_to') },
    { value: 'duplicate_of', label: t('links.relationships.duplicate_of') },
    { value: 'caused_by', label: t('links.relationships.caused_by') },
    { value: 'blocks', label: t('links.relationships.blocks') },
  ];

  const handleSelectTicket = (ticket: QmsTicket) => {
    // Check if already selected with different relationship
    const existingIndex = selectedLinks.findIndex((l) => l.ticket_id === ticket._id);

    if (existingIndex >= 0) {
      // Replace existing with new relationship
      const updated = [...selectedLinks];
      updated[existingIndex] = {
        ticket_id: ticket._id,
        ticket_title: ticket.title,
        ticket_type: ticket.type,
        relationship: selectedRelationship,
      };
      onLinksChange(updated);
    } else {
      // Add new link
      onLinksChange([
        ...selectedLinks,
        {
          ticket_id: ticket._id,
          ticket_title: ticket.title,
          ticket_type: ticket.type,
          relationship: selectedRelationship,
        },
      ]);
    }
    setSearchTerm('');
  };

  const handleRemoveLink = (ticketId: string) => {
    onLinksChange(selectedLinks.filter((l) => l.ticket_id !== ticketId));
  };

  const getRelationshipLabel = (relationship: QmsRelationshipType): string => {
    const option = relationshipOptions.find((o) => o.value === relationship);
    return option ? option.label : relationship;
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={handleToggleExpand}
        disabled={disabled}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {t('wizard.linkToExistingTickets', 'Link to Existing Tickets')}
          </span>
          <span className="text-xs text-gray-500">
            ({t('common.optional', 'Optional')})
          </span>
          {selectedLinks.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-atek-blue text-white rounded-full">
              {selectedLinks.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Search and Relationship Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.searchPlaceholder')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('links.searchHint')}
                  disabled={disabled}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent disabled:bg-gray-100"
                />
              </div>
            </div>

            {/* Relationship Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.relationshipType')}
              </label>
              <select
                value={selectedRelationship}
                onChange={(e) => setSelectedRelationship(e.target.value as QmsRelationshipType)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent disabled:bg-gray-100"
              >
                {relationshipOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search Results */}
          {searchTerm.length >= 2 && (
            <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
              {isSearching || (searchTerm !== debouncedSearchTerm) ? (
                <div className="text-center py-4 text-gray-500">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-atek-blue mx-auto"></div>
                  <p className="mt-2 text-xs">{t('links.searching')}</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">{t('links.noResults')}</p>
                </div>
              ) : (
                searchResults.map((result) => (
                  <button
                    key={result._id}
                    type="button"
                    onClick={() => handleSelectTicket(result)}
                    disabled={disabled}
                    className="w-full text-left p-2 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-atek-blue transition-colors bg-white disabled:opacity-50"
                  >
                    <p className="font-medium text-sm text-gray-900 truncate">{result.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {result.type === 'capa' ? 'CAPA' : 'Ticket'}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {result.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Selected Links */}
          {selectedLinks.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('wizard.selectedLinks', 'Selected')} ({selectedLinks.length}):
              </label>
              <div className="space-y-2">
                {selectedLinks.map((link) => (
                  <div
                    key={link.ticket_id}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {link.ticket_title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                          {getRelationshipLabel(link.relationship)}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {link.ticket_type === 'capa' ? 'CAPA' : 'Ticket'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(link.ticket_id)}
                      disabled={disabled}
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      title={t('links.unlink')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state hint */}
          {selectedLinks.length === 0 && searchTerm.length < 2 && (
            <p className="text-xs text-gray-500 text-center py-2">
              {t('wizard.linkSelectorHint', 'Search for tickets to link them to this new ticket.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
